import { BrowserWindowEvent } from 'pf-frontend-common/src/module/browser/window.event';
import { ModalViewEvent } from 'pf-frontend-common/src/module/modal/view.event';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';
import { ModalServiceInterface } from 'pf-frontend-common/src/service/modal/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { BackendConfigSettingsInterface } from 'common/data/backend/config/settings.interface';
import { settingsGetPageType } from 'common/helper/settings/get-page-type';
import { DataStoreEvent } from 'common/module/data/store.event';
import { KeyboardCharcodeEnum } from 'common/module/keyboard/charcode.enum';
import { StatsGtmProviderInterface } from 'common/module/stats/gtm/provider.interface';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { UserForgotPasswordViewStore } from 'common/module/user/forgot-password/view-store';
import { UserForgotPasswordViewStoreEvent } from 'common/module/user/forgot-password/view-store.event';
import { UserPopupContentTypeEnum } from 'common/module/user/popup/content-type.enum';
import { UserPopupOpenOptionsInterface } from 'common/module/user/popup/open-options.interface';
import { UserRegistrationViewStore } from 'common/module/user/registration/view-store';
import { UserRegistrationViewStoreEvent } from 'common/module/user/registration/view-store.event';
import { UserSignInViewStore } from 'common/module/user/sign-in/view-store';
import { UserSignInViewStoreEvent } from 'common/module/user/sign-in/view-store.event';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';
import { UserPopupServiceInterface } from 'common/service/user-popup/service.interface';
import { UserSignInStatsServiceInterface } from 'common/service/user-sign-in-stats/service.interface';

import { UserSignInStatsGtmFinishResetPassword } from '../sign-in/stats/gtm/finish-reset-password';
import { UserSignInStatsGtmSignInButtonClick } from '../sign-in/stats/gtm/signin-button-click';
import { UserSignInStatsGtmStartResetPassword } from '../sign-in/stats/gtm/start-reset-password';
import { UserSignInStatsGtmStartSignInWithFacebook } from '../sign-in/stats/gtm/start-signin-with-facebook';
import { UserSignInStatsGtmStartSignInWithGoogle } from '../sign-in/stats/gtm/start-signin-with-google';
import { UserSignInStatsGtmStartSignUpWithEmail } from '../sign-in/stats/gtm/start-signup-with-email';

export class UserPopupView implements ViewBasicInterface, UserPopupServiceInterface {
  /**
   * Is popup active
   */
  private isActive: boolean = false;

  /**
   * Constructor
   */
  constructor(
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private modalService: ModalServiceInterface,
    private windowService: WindowServiceInterface,
    private statsGtmProvider: StatsGtmProviderInterface,
    private settings: BackendConfigSettingsInterface,
    private userSignInStatsService: UserSignInStatsServiceInterface,
    private userSignInViewDynamic: ViewDynamicInterface<UserSignInViewStore>,
    private userForgotPasswordViewDynamic: ViewDynamicInterface<UserForgotPasswordViewStore>,
    private userRegistrationViewDynamic?: ViewDynamicInterface<UserRegistrationViewStore>
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(): void {
    // Initialize subviews
    this.initUserSignIn();
    if (this.userRegistrationViewDynamic) {
      this.initUserRegistration();
    }
    this.initUserForgotPassword();

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Nothing to render
  }

  /**
   * @inheritDoc
   */
  public open(options?: UserPopupOpenOptionsInterface): void {
    this.isActive = true;

    // Set sign in stats event label
    this.userSignInStatsService.setGtmEventLabel(settingsGetPageType(this.settings));

    if (options && options.callerType) {
      this.userSignInStatsService.setGtmEventLabel(
        this.userSignInStatsService.getGtmEventLabel() + ' - ' + options.callerType
      );
    }

    if (options && options.contentType === UserPopupContentTypeEnum.registration) {
      return this.userRegistrationViewDynamic && this.openRegistrationModal();
    }
    // Reset sign in form
    this.userSignInViewDynamic.getViewStore().reset();

    // Show sign in popup
    this.userSignInViewDynamic.render();

    // Open modal
    this.modalService.open();

    // Log sign in gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmSignInButtonClick(this.userSignInStatsService.getGtmEventLabel()));
  }

  /**
   * @inheritDoc
   */
  public isPopupOpen(): boolean {
    return this.isActive;
  }

  /**
   * sets user sign in event reference
   * @param eventReference
   */
  public setEventReference(eventReference: StatsTealiumDataLayerEventReferenceEnum) {
    this.userSignInViewDynamic.getViewStore().setEventReference(eventReference);
  }

  /**
   * Close popup
   */
  private close(): void {
    if (!this.isActive) {
      return;
    }
    this.userRegistrationViewDynamic?.getViewStore().updateLoaderEnabled(false);
    this.modalService.close();
    this.isActive = false;
  }

  /**
   * @inheritDoc
   */
  private openRegistrationModal(): void {
    // Reset registration form
    this.userRegistrationViewDynamic?.getViewStore().reset();

    // Show registration popup
    this.userRegistrationViewDynamic?.render();

    // Open modal
    this.modalService.open();

    // Log create account gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmStartSignUpWithEmail(this.userSignInStatsService.getGtmEventLabel()));
  }

  /**
   * @inheritDoc
   */
  private observers(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInStart, this.onSignInStart);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInFailed, this.onSignInFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInSucceeded, this.onSignInSucceeded);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInValidationFailed, this.onSignInValidationFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationStart, this.onRegistrationStart);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationFailed, this.onRegistrationFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationSucceeded, this.onRegistrationSucceeded);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationValidationFailed, this.onRegistrationValidationFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetStart, this.onAskPasswordResetStart);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetFailed, this.onAskPasswordResetFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetSucceeded, this.onAskPasswordResetSucceeded);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetValidationFailed, this.onAskPasswordResetValidationFailed);

    this.modalService.getEventEmitter().addListener(ModalViewEvent.close, this.onCloseModalView);

    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.keyup, this.onKeyupWindow);
  }

  /**
   * Init user sign in
   */
  private initUserSignIn(): void {
    this.userSignInViewDynamic.initialize({
      element: this.modalService.getContentElement(),
    });

    this.userSignInViewDynamic.getViewStore().initialize();
    this.userSignInViewDynamic.render();

    this.userSignInViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(UserSignInViewStoreEvent.clickCreateAccount, this.onClickCreateAccount);

    this.userSignInViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(UserSignInViewStoreEvent.clickForgotPassword, this.onClickForgotPassword);

    this.userSignInViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(UserSignInViewStoreEvent.clickClose, this.onClickClose);

    this.userSignInViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(UserSignInViewStoreEvent.clickSignInWithFacebook, this.onClickSignInWithFacebook);

    this.userSignInViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(UserSignInViewStoreEvent.clickSignInWithGoogle, this.onClickSignInWithGoogle);
  }

  /**
   * Init registration
   */
  private initUserRegistration(): void {
    this.userRegistrationViewDynamic?.initialize({
      element: this.modalService.getContentElement(),
    });

    this.userRegistrationViewDynamic?.getViewStore().initialize();

    this.userRegistrationViewDynamic
      ?.getViewStore()
      .getEventEmitter()
      .addListener(DataStoreEvent.updateState, this.onUpdateStateUserRegistrationViewStore);

    this.userRegistrationViewDynamic
      ?.getViewStore()
      .getEventEmitter()
      .addListener(UserRegistrationViewStoreEvent.clickLogin, this.onClickLogin);

    this.userRegistrationViewDynamic
      ?.getViewStore()
      .getEventEmitter()
      .addListener(UserRegistrationViewStoreEvent.clickClose, this.onClickClose);
  }

  /**
   * Init user forgot password
   */
  private initUserForgotPassword(): void {
    this.userForgotPasswordViewDynamic.initialize({
      element: this.modalService.getContentElement(),
    });

    this.userForgotPasswordViewDynamic.getViewStore().initialize();

    this.userForgotPasswordViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(UserForgotPasswordViewStoreEvent.clickClose, this.onClickClose);
  }

  /**
   * User registration view store state updated
   */
  private onUpdateStateUserRegistrationViewStore = () => {
    this.userRegistrationViewDynamic?.render();
  };

  /**
   * Click on create account button from sign in popup
   */
  private onClickCreateAccount = () => {
    this.userRegistrationViewDynamic?.render();

    // Log start create account gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmStartSignUpWithEmail(this.userSignInStatsService.getGtmEventLabel()));
  };

  /**
   * Click sign in with facebook button
   */
  private onClickSignInWithFacebook = () => {
    // Log start signin with facebook
    this.statsGtmProvider.send(
      UserSignInStatsGtmStartSignInWithFacebook(this.userSignInStatsService.getGtmEventLabel())
    );
  };

  /**
   * Click sign in with google button
   */
  private onClickSignInWithGoogle = () => {
    // Log start signin with google
    this.statsGtmProvider.send(UserSignInStatsGtmStartSignInWithGoogle(this.userSignInStatsService.getGtmEventLabel()));
  };

  /**
   * Click on Forgot password? button from sign in popup
   */
  private onClickForgotPassword = () => {
    this.userForgotPasswordViewDynamic.render();

    // Log start reset password gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmStartResetPassword(this.userSignInStatsService.getGtmEventLabel()));
  };

  /**
   * Click on close button
   */
  private onClickClose = () => {
    this.close();
  };

  /**
   * Click on log in button from new user popup
   */
  private onClickLogin = () => {
    this.userSignInViewDynamic.render();
  };

  /**
   * Sign in started
   */
  private onSignInStart = () => {
    if (!this.isActive) {
      return;
    }

    this.userSignInViewDynamic.getViewStore().updateLoaderEnabled(true);
  };

  /**
   * Sign in failed
   */
  private onSignInFailed = () => {
    if (!this.isActive) {
      return;
    }
    this.userSignInViewDynamic.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * Sign in succeeded
   */
  private onSignInSucceeded = () => {
    if (!this.isActive) {
      return;
    }

    this.userSignInViewDynamic.getViewStore().updateLoaderEnabled(false);

    this.close();
  };

  /**
   * Sign in validation failed
   */
  private onSignInValidationFailed = () => {
    if (!this.isActive) {
      return;
    }
    this.userSignInViewDynamic.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * Registration started
   */
  private onRegistrationStart = () => {
    if (!this.isActive) {
      return;
    }
    this.userRegistrationViewDynamic?.getViewStore().updateLoaderEnabled(true);
  };

  /**
   * Registration failed
   */
  private onRegistrationFailed = () => {
    if (!this.isActive) {
      return;
    }
    this.userRegistrationViewDynamic?.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * Registration succeeded
   */
  private onRegistrationSucceeded = () => {
    if (!this.isActive) {
      return;
    }

    this.close();
  };

  /**
   * Registration validation failed
   */
  private onRegistrationValidationFailed = () => {
    if (!this.isActive) {
      return;
    }
    this.userRegistrationViewDynamic?.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * Password reset started
   */
  private onAskPasswordResetStart = () => {
    if (!this.isActive) {
      return;
    }
    this.userForgotPasswordViewDynamic.getViewStore().updateLoaderEnabled(true);
  };

  /**
   * Password reset failed
   */
  private onAskPasswordResetFailed = () => {
    if (!this.isActive) {
      return;
    }
    this.userForgotPasswordViewDynamic.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * Password reset succeeded
   */
  private onAskPasswordResetSucceeded = () => {
    if (!this.isActive) {
      return;
    }
    // Log finish reset password gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmFinishResetPassword(this.userSignInStatsService.getGtmEventLabel()));

    this.userForgotPasswordViewDynamic.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * Password reset validation failed
   */
  private onAskPasswordResetValidationFailed = () => {
    if (!this.isActive) {
      return;
    }
    this.userForgotPasswordViewDynamic.getViewStore().updateLoaderEnabled(false);
  };

  /**
   * On close modal view
   */
  private onCloseModalView = () => {
    this.isActive = false;
  };

  /**
   * Fired document keyup event
   */
  private onKeyupWindow = (e: KeyboardEvent) => {
    if (this.isActive && e.keyCode === KeyboardCharcodeEnum.escape) {
      this.close();
    }
  };
}
